import React, { useState, useRef, useEffect } from 'react';
import { Button, Modal, ModalHeader, ModalBody } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export default function ImageModal(props) {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const touchStartDistance = useRef(0);
  const lastTouchDistance = useRef(0);

  const MIN_SCALE = 1;
  const MAX_SCALE = 5;
  const ZOOM_STEP = 0.3;

  // Reset zoom when modal opens/closes or image changes
  useEffect(() => {
    if (props.isOpen) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  }, [props.isOpen, props.imgSrc]);

  const handleZoomIn = () => {
    setScale((prevScale) => Math.min(prevScale + ZOOM_STEP, MAX_SCALE));
  };

  const handleZoomOut = () => {
    const newScale = Math.max(scale - ZOOM_STEP, MIN_SCALE);
    setScale(newScale);
    if (newScale === MIN_SCALE) {
      setPosition({ x: 0, y: 0 });
    }
  };

  const handleReset = () => {
    setScale(MIN_SCALE);
    setPosition({ x: 0, y: 0 });
  };

  // Mouse wheel zoom
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    const newScale = Math.min(Math.max(scale + delta, MIN_SCALE), MAX_SCALE);
    setScale(newScale);
    if (newScale === MIN_SCALE) {
      setPosition({ x: 0, y: 0 });
    }
  };

  // Mouse drag handlers
  const handleMouseDown = (e) => {
    if (scale > MIN_SCALE) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && scale > MIN_SCALE) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch handlers for pinch-to-zoom
  const getTouchDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      // Pinch zoom
      const distance = getTouchDistance(e.touches);
      touchStartDistance.current = distance;
      lastTouchDistance.current = distance;
    } else if (e.touches.length === 1 && scale > MIN_SCALE) {
      // Single touch drag
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y,
      });
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2) {
      // Pinch zoom
      e.preventDefault();
      const distance = getTouchDistance(e.touches);
      const delta = distance - lastTouchDistance.current;
      const scaleDelta = delta * 0.01;

      const newScale = Math.min(Math.max(scale + scaleDelta, MIN_SCALE), MAX_SCALE);
      setScale(newScale);
      lastTouchDistance.current = distance;

      if (newScale === MIN_SCALE) {
        setPosition({ x: 0, y: 0 });
      }
    } else if (e.touches.length === 1 && isDragging && scale > MIN_SCALE) {
      // Single touch drag
      e.preventDefault();
      setPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y,
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    touchStartDistance.current = 0;
    lastTouchDistance.current = 0;
  };

  // Double click to reset
  const handleDoubleClick = () => {
    handleReset();
  };

  return (
    <>
      <style>
        {`
          .image-modal-content-95vh {
            height: 95vh !important;
            max-height: 95vh !important;
          }
        `}
      </style>
      <Modal
        isOpen={props.isOpen}
        centered
        zIndex={2000}
        className='modal-dialog modal-xl text-center'
        contentClassName='image-modal-content-95vh'
        fullscreen={'xl'}
      >
        <ModalHeader toggle={props.parentRef}>{props.planName}</ModalHeader>
        <ModalBody className='inputs-icons' style={{ display: 'flex', flexDirection: 'column', height: 'calc(95vh - 56px)', padding: '1rem' }}>
          <div
            ref={containerRef}
            style={{
              position: 'relative',
              overflow: 'hidden',
              flex: '1',
              minHeight: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: scale > MIN_SCALE ? (isDragging ? 'grabbing' : 'grab') : 'default',
              touchAction: 'none',
            }}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onDoubleClick={handleDoubleClick}
          >
            <img
              ref={imageRef}
              className='img-fluid center'
              src={props.imgSrc}
              style={{
                maxHeight: '100%',
                maxWidth: '100%',
                objectFit: 'contain',
                transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                userSelect: 'none',
                pointerEvents: 'none',
              }}
              draggable={false}
            />
          </div>

          {/* Zoom Controls */}
          <div style={{ marginTop: '10px', marginBottom: '5px', flexShrink: 0 }}>
            <Button
              color='primary'
              size='sm'
              onClick={handleZoomIn}
              disabled={scale >= MAX_SCALE}
              style={{ marginRight: '10px' }}
            >
              <FontAwesomeIcon icon={['fas', 'plus']} /> Zoom In
            </Button>
            <Button
              color='primary'
              size='sm'
              onClick={handleZoomOut}
              disabled={scale <= MIN_SCALE}
              style={{ marginRight: '10px' }}
            >
              <FontAwesomeIcon icon={['fas', 'minus']} /> Zoom Out
            </Button>
            <Button
              color='secondary'
              size='sm'
              onClick={handleReset}
              disabled={scale === MIN_SCALE}
              style={{ marginRight: '10px' }}
            >
              <FontAwesomeIcon icon={['fas', 'undo']} /> Reset
            </Button>
            <span style={{ fontSize: '14px', color: '#666' }}>
              {Math.round(scale * 100)}%
            </span>
          </div>

          <div style={{ textAlign: 'center', flexShrink: 0, marginTop: '5px' }}>
            <Button
              color='green'
              className='button-dashboard'
              onClick={() => props.setIsOpen(!props.isOpen)}
            >
              Close
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
}
